import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useState,
  DragEvent,
} from 'react';
import styled from 'styled-components';
import UploadInput, {
  getBase24,
  convertToWebp,
} from '@components/dataEntry/upload/UploadInput';
import CircleSpinner from '@components/dataDisplay/spinners/CircleSpinner';
import PreviewSec from '@components/dataDisplay/board/PreviewSec';
import Icon from '@components/dataDisplay/Icons/Icon';
import { useInputs } from '@hooks/useInputs';
import TextInput from '@components/dataEntry/Input/TextInput';
import LabelSet from '../Input/Label/LabelSet';
import { validateGif } from '@utils/validateGif';

const UploadImgSec = styled.div<{
  minHeight?: number | string;
  isDragging?: boolean;
}>`
  border: 2px dashed
    ${({ isDragging }) =>
    isDragging ? 'var(--primaryColor)' : 'var(--lineColor)'};
  border-radius: var(--cardBorderRadius);
  background-color: ${({ isDragging }) =>
    isDragging
      ? 'rgba(var(--primaryColorRGB), 0.1)'
      : 'var(--cardBackgroundColor)'};
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  width: 100%;
  height: ${({ minHeight }) =>
    typeof minHeight === 'number' ? `${minHeight}px` : minHeight || '100px'};
  min-height: ${({ minHeight }) =>
    typeof minHeight === 'number' ? `${minHeight}px` : minHeight || '100px'};
  transition: background-color 0.2s, border-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 480px) {
    min-height: ${({ minHeight }) =>
    typeof minHeight === 'number' ? `${minHeight}px` : minHeight || '100px'};
    height: 100% !important;
  }
`;
const Contents = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  color: var(--subTextColor);
`;

type Props = {
  id: string;
  setSelectedFile: any;
  isUploading?: boolean;
  setIsUploading?: any;
  children?: JSX.Element | JSX.Element[];
  text?: string;
  fileReset?: () => void;
  accept?: string;
  customDelete?: () => void;
  prevCover?: boolean;
  customSetPreviewImgURL?: any;
  label?: string;
  style?: CSSProperties;
  prevImgURL?: string | null;
  setURL?: any;
  prevURL?: string;
  minHeight?: number | string;
  required?: boolean;
  description?: string;
  direction?: 'row' | 'column';
  align?: 'left' | 'center' | 'right'; // 🆕 정렬 옵션
};

function CustomUploadBox({
  id,
  children,
  setSelectedFile,
  isUploading,
  prevImgURL,
  fileReset,
  accept,
  customDelete,
  customSetPreviewImgURL,
  prevCover,
  text = '이미지를 드래그&드롭 또는 업로드',
  label,
  style,
  setURL,
  prevURL,
  minHeight,
  required,
  description,
  direction = 'column',
  align = 'left',
}: Props) {
  const [previewURL, setPreviewImgURL] = useState<string | null>(
    prevImgURL || '',
  );
  const [uploading, setUploading] = useState(isUploading || false);
  const [isDragging, setIsDragging] = useState(false);

  const [{ url }, handleChange, reset] = useInputs({
    url: prevURL || '',
  });

  const fileDefaultReset = useCallback(() => {
    if (customDelete) {
      customDelete();
    } else {
      if (fileReset) {
        fileReset();
      }
    }
    setSelectedFile(null);
    setPreviewImgURL(null);
  }, [fileReset, customDelete, setSelectedFile]);

  // 드래그 앤 드롭 파일 처리
  const processDroppedFile = useCallback(
    async (file: File) => {
      if (!file) return;

      // 파일 용량 제한 (50MB)
      const fileSize = file.size;
      const limit = 51000000;
      if (fileSize > limit) {
        alert('파일용량은 50mb 를 넘을수 없습니다.');
        return;
      }

      setUploading(true);
      const isGif = validateGif(file);
      const isSvg = file.type === 'image/svg+xml';

      if (accept || isGif || isSvg) {
        // 일반 파일 처리 (GIF, SVG, accept 지정 파일은 변환 없이 그대로 사용)
        const base24URL = await getBase24(file);
        setSelectedFile(file);
        setPreviewImgURL(base24URL as string);
        if (customSetPreviewImgURL) customSetPreviewImgURL(base24URL);
      } else {
        // webp 변환 처리
        const base24URL = await getBase24(file);
        const convertWebpFile = await convertToWebp(base24URL, true);
        const preview = await convertToWebp(base24URL, false);
        setSelectedFile(convertWebpFile);
        setPreviewImgURL(preview as string);
        if (customSetPreviewImgURL) customSetPreviewImgURL(preview);
      }
      setUploading(false);
    },
    [accept, setSelectedFile, customSetPreviewImgURL],
  );

  // 드래그 이벤트 핸들러
  const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        const file = files[0];
        // 이미지 파일인지 확인
        if (
          file.type.startsWith('image/') ||
          (accept && file.type.match(accept.replace('*', '.*')))
        ) {
          processDroppedFile(file);
        } else {
          alert('이미지 파일만 업로드 가능합니다.');
        }
      }
    },
    [accept, processDroppedFile],
  );

  useEffect(() => {
    // if (setURL) setURL(url || '');
    setPreviewImgURL(prevImgURL || '');
  }, [prevImgURL, url, setURL, setPreviewImgURL]);

  return (
    <div style={{ display: 'flex', flexDirection: direction }}>
      {label && (
        <LabelSet
          required={required}
          label={label}
          description={description}
          align={align}
        />
      )}
      {previewURL ? (
        <PreviewSec
          imgURL={previewURL}
          reset={fileDefaultReset}
          cover={prevCover}
          style={style}
          minHeight={minHeight}
        />
      ) : (
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <UploadInput
            id={id}
            setSelectedFile={setSelectedFile}
            setPreviewImgURL={setPreviewImgURL}
            customSetPreviewImgURL={customSetPreviewImgURL}
            setIsUploading={setUploading}
            type="custom"
            accept={accept}
          >
            <UploadImgSec
              style={style}
              minHeight={minHeight}
              isDragging={isDragging}
            >
              {uploading ? (
                <CircleSpinner size="xl" />
              ) : (
                <Contents>
                  <Icon
                    name={isDragging ? 'DownArrow20' : 'Plus14'}
                    color="var(--subTextColor)"
                  />
                  {isDragging ? '파일을 여기에 놓으세요' : children || text}
                </Contents>
              )}
            </UploadImgSec>
          </UploadInput>
        </div>
      )}
      {setURL && (
        <TextInput
          placeholder="https://..."
          name={'url'}
          value={url}
          onChange={handleChange}
        />
      )}
    </div>
  );
}

export default React.memo(CustomUploadBox);
